import { useState } from "react";
import { ChevronDown, Info } from "lucide-react";

const pct = (a, b) => (b > 0 ? Math.round((100 * a) / b) : null);

// Legenda spiegata del grafico mensile produzione/consumo, per il cliente finale.
export function EnergyChartLegend({ data = [], colors, hasBattery = false, print = false }) {
  const [open, setOpen] = useState(false);
  const sum = (k) => data.reduce((s, m) => s + (Number(m[k]) || 0), 0);
  const prod = sum("produzione"), cons = sum("consumo"), auto = sum("autoconsumo");
  const usata = pct(auto, prod), copre = pct(auto, cons);
  const batt = hasBattery ? ", direttamente o tramite la batteria" : " direttamente";
  const items = [
    ["Produzione FV", colors.prod, "è tutta l'energia elettrica generata dai pannelli fotovoltaici durante le ore di luce."],
    ["Consumo", colors.cons, "è tutta l'energia elettrica necessaria alla tua abitazione, sia di giorno che di notte."],
    ["Autoconsumo", colors.auto, `è la parte dell'energia prodotta dal fotovoltaico che riesci a utilizzare in casa${batt}.`],
    ["Energia acquistata", colors.acq, `è l'elettricità che devi prelevare dalla rete quando il fotovoltaico${hasBattery ? " e la batteria non sono sufficienti" : " non è sufficiente"}.`],
  ];
  const [legendOpen, setLegendOpen] = useState(false);
  const showWhy = print || open;
  if (!print && !legendOpen) {
    return (
      <button type="button" onClick={() => setLegendOpen(true)} className="mt-4 flex w-full items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 text-sm font-semibold text-left hover:bg-primary/15 transition-colors" data-testid="energy-legend-toggle">
        <span className="flex items-center gap-2"><Info className="h-4 w-4 text-primary" /> Come leggere il grafico{usata != null ? ` · nel tuo caso usi in casa il ${usata}% dell'energia prodotta` : ""}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-primary" />
      </button>
    );
  }
  return (
    <div className="mt-4 rounded-lg border border-border p-4 text-sm space-y-3 pdf-atomic" data-testid="energy-chart-legend">
      {!print && (
        <button type="button" onClick={() => setLegendOpen(false)} className="flex w-full items-center justify-between font-semibold text-left text-primary hover:opacity-80 transition-opacity" data-testid="energy-legend-collapse">
          <span className="flex items-center gap-2"><Info className="h-4 w-4" /> Come leggere il grafico</span>
          <ChevronDown className="h-4 w-4 rotate-180" />
        </button>
      )}
      <p className="text-muted-foreground">Il grafico mostra, mese per mese, come viene prodotta e utilizzata l'energia elettrica della tua abitazione.</p>
      <ul className="space-y-1.5">
        {items.map(([label, color, text]) => (
          <li key={label} className="flex gap-2" data-testid={`legend-item-${label.split(" ")[0].toLowerCase()}`}>
            <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
            <span><b>{label}</b>: {text}</span>
          </li>
        ))}
      </ul>
      {usata != null && (
        <p className="rounded-md bg-primary/10 border border-primary/30 px-3 py-2" data-testid="legend-tuo-caso">
          <b>Nel tuo caso:</b> il <b>{usata}%</b> dell'energia prodotta la usi in casa{hasBattery ? " (anche grazie alla batteria)" : ""}, il <b>{100 - usata}%</b> va in rete; il fotovoltaico copre il <b>{copre}%</b> dei tuoi consumi.
        </p>
      )}
      <div className="border-t border-border pt-3">
        {print ? (
          <div className="font-semibold">Perché non riesci a utilizzare tutta l'energia prodotta?</div>
        ) : (
          <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between font-semibold text-left hover:text-primary transition-colors" data-testid="legend-why-toggle">
            Perché non riesci a utilizzare tutta l'energia prodotta?
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
        {showWhy && (
          <div className="mt-2 space-y-1.5 text-muted-foreground" data-testid="legend-why-text">
            <p>Perché produzione e consumo non avvengono sempre nello stesso momento. I pannelli producono durante il giorno, mentre la casa consuma energia anche la sera e di notte.</p>
            <p>Quando produci più di quanto consumi, l'energia in eccesso {hasBattery ? "viene immagazzinata nella batteria e, quando è piena, immessa nella rete elettrica" : "viene immessa nella rete elettrica"}.</p>
            <p>Quando invece la produzione{hasBattery ? " e l'energia accumulata non bastano" : " non basta"}, acquisti elettricità dalla rete.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export const withAcquistata = (rows) => (rows || []).map((m) => ({ ...m, acquistata: Math.max(0, Math.round((Number(m.consumo) || 0) - (Number(m.autoconsumo) || 0))) }));
